//  src/shared/cache/moduleDataCache.js
// Caches inner-module data (events/images/announcements) across iterations.

const mem = new Map();            // key -> { value, cachedAt }
const iqamahReadyMem = new Map(); // iqamahKey -> true

const isBrowser = typeof window !== "undefined";

function lsGet(key) {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function lsSet(key, value) {
  if (!isBrowser) return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// --- Module data cache (per module id) -----------------
export function moduleKey(mod) {
  // Prefer a stable id; fallback to a composite if id is missing.
  return mod?.id != null ? `mod:${mod.id}` : `mod:${mod?.source_Module_Name}:${mod?.sequence_No}`;
}

export function getModuleCached(mod) {
  const key = moduleKey(mod);
  const inMem = mem.get(key);
  if (inMem) return inMem.value;

  const inLs = lsGet(key);
  if (inLs && typeof inLs === "object") {
    mem.set(key, inLs);
    return inLs.value ?? null;
  }
  return null;
}

export function setModuleCached(mod, value) {
  const key = moduleKey(mod);
  const entry = { value, cachedAt: Date.now() };
  mem.set(key, entry);
  lsSet(key, entry);
}

// Convenience: get or load (and cache) a module’s data
export async function getOrLoadModule(mod, loaderFn) {
  const cached = getModuleCached(mod);
  if (cached != null) return cached; // empty array is a valid value too
  const value = await loaderFn();
  setModuleCached(mod, value);
  return value;
}

// --- Iqamah readiness cache (per iqamah screen signature) -----------
export function iqamahKey(screenNode, children) {
  const idPart = screenNode?.id ?? screenNode?.iqama_Screen_Name ?? "iqamah";
  // Signature from child identities to detect config changes:
  const childSig = (children || [])
    .map(c => `${c.id ?? c.source_Module_Name}:${c.sequence_No ?? 0}`)
    .sort()
    .join("|");
  return `iqamah:${idPart}:${childSig}`;
}

export function getIqamahReady(iKey) {
  if (iqamahReadyMem.get(iKey)) return true;
  const ls = lsGet(iKey);
  if (ls && ls.ready === true) {
    iqamahReadyMem.set(iKey, true);
    return true;
  }
  return false;
}

export function setIqamahReady(iKey, ready = true) {
  if (ready) {
    iqamahReadyMem.set(iKey, true);
    lsSet(iKey, { ready: true, at: Date.now() });
  } else {
    iqamahReadyMem.delete(iKey);
    if (isBrowser) localStorage.removeItem(iKey);
  }
}

// Return true if a module already has cached data (even an empty array)
export function hasModuleCached(mod) {
  const v = getModuleCached(mod);
  return v !== null && v !== undefined;
}
